import React from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import {
  formatMetricValue,
  orbitResultFormat,
  orbitMetricName,
  seriesOf,
  seriesPointLabel
} from '../chartAdapters';
import { TOrbit } from '../i18n/TModules';
import { ORBIT_PAGE_SIZE, useOrbitPaging } from '../pagination';
import { OrbitAnswerLabel, OrbitDataTable, orbitTokens, orbitType } from '../styles';
import { OrbitMetricResult } from '../types';
import OrbitPagingFooter from './OrbitPagingFooter';

export interface IOrbitSeriesTableProps {
  result: OrbitMetricResult;
  /** The tenant's reporting currency, for a metric the catalogue states as an amount. */
  currency?: string;
}

const Root = styled.div`
  > * + * {
    margin-top: 6px;
  }
`;

const Disclosure = styled.details`
  > * + * {
    margin-top: 6px;
  }
`;

const Summary = styled.summary`
  color: ${orbitTokens.action};
  cursor: pointer;
  ${orbitType.containerBody}
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${orbitTokens.action};
    outline-offset: 1px;
  }
`;

const Muted = styled.span`
  color: ${orbitTokens.textMuted};
`;

/**
 * The readable form of a metric's `series[]`. Today the distribution is only ever charted, so a
 * 30-point series becomes 30 unlabelled slices and its numbers are unreadable anywhere.
 */
const OrbitSeriesTable: React.FC<IOrbitSeriesTableProps> = ({ result, currency }) => {
  const { t } = useMultiLanguageTranslation();
  const points = seriesOf(result);
  const format = orbitResultFormat(result.metric_id, result.cohort);
  const paging = useOrbitPaging(points.length);

  if (points.length === 0) {
    return null;
  }

  const table = (
    <>
      <OrbitDataTable data-testid={`orbit-series-table-${result.metric_id}`}>
        <thead>
          <tr>
            <th scope="col">{t(`${TOrbit}.metrics.columnKey`)}</th>
            <th scope="col">{t(`${TOrbit}.metrics.columnValue`)}</th>
            <th scope="col">{t(`${TOrbit}.metrics.columnCustomers`)}</th>
          </tr>
        </thead>
        <tbody>
          {points.slice(0, paging.shown).map(point => (
            <tr key={point.key} data-testid={`orbit-series-row-${result.metric_id}`}>
              <th scope="row">{seriesPointLabel(result, point.key)}</th>
              {/*
                `formatMetricValue` answers the placeholder for a null, where it used to answer `0`:
                `Intl.NumberFormat.format(null)` coerces to zero, so an unmeasured period printed as
                a measurement of nothing (PLA-9563 §6c).
              */}
              <td>
                {point.value === null ? (
                  <Muted>{t(`${TOrbit}.metrics.noValue`)}</Muted>
                ) : (
                  formatMetricValue(point.value, format, currency)
                )}
              </td>
              <td>
                {point.n_customers === null ? (
                  <Muted>{t(`${TOrbit}.metrics.noValue`)}</Muted>
                ) : (
                  formatMetricValue(point.n_customers)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </OrbitDataTable>
      <OrbitPagingFooter paging={paging} testId={`orbit-series-paging-${result.metric_id}`} />
    </>
  );

  // A series that fits the first page is worth showing outright; a longer one would otherwise push
  // the rest of the transcript off screen, so it opens on request.
  return (
    <Root>
      <OrbitAnswerLabel title={result.metric_id}>
        {orbitMetricName(result.metric_id, result.metric_name)}
      </OrbitAnswerLabel>
      {points.length > ORBIT_PAGE_SIZE ? (
        <Disclosure data-testid={`orbit-series-disclosure-${result.metric_id}`}>
          <Summary>{t(`${TOrbit}.metrics.showBreakdown`, { count: points.length })}</Summary>
          {table}
        </Disclosure>
      ) : (
        table
      )}
    </Root>
  );
};

export default OrbitSeriesTable;
